import { useEffect, useId, useRef, useState } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { formatarDataHora } from '@/lib/formato';

import { abrirFoto, useEnviarFoto, useFotoDoEquipamento, useRemoverFoto } from './consultas-frota';
import type { EquipamentoDaFrota } from './frota';
import {
  NAO_ABRE,
  ProblemaDaFoto,
  descreverMedidas,
  descreverTamanho,
  prepararFoto,
  type FotoPreparada,
} from './imagem';
import { MensagemDeErro } from './MensagemDeErro';
import { MiniaturaDoEquipamento } from './MiniaturaDoEquipamento';

/**
 * A foto do equipamento, no alto da aba Ficha (ADR-007, "O primeiro recurso no
 * padrao"). E aqui que a pessoa acha onde subir a foto: Equipamentos → clicar
 * na maquina → aba Ficha → Foto.
 *
 * ## O caminho
 *
 * Escolher → PREPARAR no navegador (reduz a 2.048 px, regrava em JPEG sem
 * EXIF nem GPS, gera a miniatura de 64 px) → PREVIA, com a miniatura como a
 * lista vai mostrar → Salvar foto. Nada sobe antes de a pessoa ver o que vai
 * subir: a foto de celular deitada, o recorte da miniatura cortando a maquina
 * ao meio, tudo aparece antes de virar registro.
 *
 * ## Quem faz o que
 *
 * Enviar, trocar e remover pedem `equipamento.gerir` — a mesma permissao de
 * editar a ficha; o servidor recusaria o resto. Ver e abrir a foto e de todo
 * mundo que ve a maquina.
 *
 * Nenhum botao laranja: "Cadastrar equipamento" continua a unica acao
 * primaria da tela (regra 1). Trocar e remover NAO apagam: a foto anterior
 * sai da ficha e fica na linha do tempo da maquina.
 */

/* O que o navegador costuma saber abrir. HEIC so abre no Safari; nos outros,
   a preparacao recusa com a frase que pede JPEG ou PNG. */
const TIPOS_ACEITOS = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

interface Previa {
  readonly preparada: FotoPreparada;
  /** Endereco local da miniatura, para mostrar como ela aparece na lista. */
  readonly miniaturaUrl: string;
}

function liberar(previa: Previa | null) {
  if (!previa) return;
  URL.revokeObjectURL(previa.preparada.previa);
  URL.revokeObjectURL(previa.miniaturaUrl);
}

export function FotoDoEquipamento({
  equipamento,
  podeEditar,
}: {
  readonly equipamento: EquipamentoDaFrota;
  readonly podeEditar: boolean;
}) {
  const titulo = useId();
  const campo = useRef<HTMLInputElement>(null);
  const fotoId = equipamento.foto?.id ?? null;
  const consulta = useFotoDoEquipamento(equipamento.id, fotoId);
  const enviar = useEnviarFoto();
  const remover = useRemoverFoto();

  const [preparando, setPreparando] = useState(false);
  const [previa, setPrevia] = useState<Previa | null>(null);
  /** Problema da imagem visto no navegador, antes de subir. */
  const [problema, setProblema] = useState<string | null>(null);
  /** Recusa do servidor: `{ erro, comoResolver }` chega pela `ErroDaApi`. */
  const [recusa, setRecusa] = useState<unknown>(null);
  const [miniaturaFalhou, setMiniaturaFalhou] = useState(false);
  /** A URL que nao carregou (assinatura vencida, rede). A seguinte e outra. */
  const [imagemFalhou, setImagemFalhou] = useState<string | null>(null);
  /** A URL que ja abriu: antes dela, o lugar da foto e esqueleto. */
  const [imagemAberta, setImagemAberta] = useState<string | null>(null);

  /* Os enderecos `blob:` da previa seguram a imagem na memoria da aba ate
     serem revogados. Revoga ao trocar, ao descartar, ao salvar e ao sair. */
  const previaAtual = useRef<Previa | null>(null);
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      liberar(previaAtual.current);
      previaAtual.current = null;
    };
  }, []);

  function trocarPrevia(nova: Previa | null) {
    liberar(previaAtual.current);
    previaAtual.current = nova;
    setPrevia(nova);
  }

  function escolherArquivo() {
    campo.current?.click();
  }

  function aoEscolher(arquivo: File | null) {
    /* Limpa o campo: escolher o MESMO arquivo de novo (depois de descartar a
       previa, ou para completar a miniatura) tem de disparar a troca. */
    if (campo.current) campo.current.value = '';
    if (!arquivo) return;
    setProblema(null);
    setRecusa(null);
    setMiniaturaFalhou(false);
    setPreparando(true);
    prepararFoto(arquivo)
      .then((preparada) => {
        const nova = { preparada, miniaturaUrl: URL.createObjectURL(preparada.miniatura) };
        if (!montado.current) {
          liberar(nova);
          return;
        }
        trocarPrevia(nova);
      })
      .catch((falha: unknown) => {
        if (montado.current) setProblema(falha instanceof ProblemaDaFoto ? falha.message : NAO_ABRE);
      })
      .finally(() => {
        if (montado.current) setPreparando(false);
      });
  }

  function salvar() {
    if (!previa) return;
    setRecusa(null);
    enviar.mutate(
      { equipamentoId: equipamento.id, preparada: previa.preparada },
      {
        onSuccess: (enviada) => {
          trocarPrevia(null);
          setImagemFalhou(null);
          setMiniaturaFalhou(enviada.miniaturaFalhou);
        },
        onError: (falha) => {
          setRecusa(falha);
        },
      },
    );
  }

  function removerFoto() {
    setRecusa(null);
    remover.mutate(equipamento.id, {
      onSuccess: () => {
        setMiniaturaFalhou(false);
      },
      onError: (falha) => {
        setRecusa(falha);
      },
    });
  }

  function abrir() {
    setRecusa(null);
    abrirFoto(equipamento.id).catch((falha: unknown) => {
      setRecusa(falha);
    });
  }

  const ocupado = preparando || enviar.isPending || remover.isPending;
  const dados = consulta.data;
  /* A miniatura que ficou para tras aparece a quem pode completa-la: logo
     depois do envio, ou depois, ao abrir a ficha de novo. */
  const avisarMiniatura =
    podeEditar && previa === null && fotoId !== null && (miniaturaFalhou || dados?.foto.temMiniatura === false);

  return (
    <section aria-labelledby={titulo} className="flex flex-col gap-3">
      <h3 id={titulo} className="txt-rotulo text-tinta-secundaria">
        Foto
      </h3>

      {podeEditar ? (
        <input
          ref={campo}
          type="file"
          accept={TIPOS_ACEITOS}
          tabIndex={-1}
          aria-label={`Escolher a foto de ${equipamento.codigo}`}
          className="sr-only"
          onChange={(e) => {
            aoEscolher(e.target.files?.[0] ?? null);
          }}
        />
      ) : null}

      {previa ? (
        <div className="flex flex-col gap-3">
          <img
            src={previa.preparada.previa}
            alt={`Prévia da foto de ${equipamento.codigo}`}
            className="bloco-rebaixado max-h-48 w-full object-contain"
          />
          <div className="flex items-center gap-3">
            <MiniaturaDoEquipamento foto={{ id: 'previa', miniaturaUrl: previa.miniaturaUrl, enviadoEm: null }} />
            <span className="flex min-w-0 flex-col">
              <span className="txt-apoio text-tinta-secundaria">Assim ela aparece na lista</span>
              <span className="txt-apoio text-tinta-apagada">
                {`${descreverMedidas(previa.preparada.largura, previa.preparada.altura)} · ${descreverTamanho(previa.preparada.principal.size)}`}
              </span>
            </span>
          </div>
          {fotoId !== null ? (
            <p className="txt-apoio text-pretty text-tinta-secundaria">
              A foto atual sai da ficha. A troca fica na linha do tempo da máquina.
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Botao
              nivel="terciario"
              disabled={enviar.isPending}
              onClick={() => {
                trocarPrevia(null);
              }}
            >
              Descartar prévia
            </Botao>
            <Botao nivel="secundario" disabled={enviar.isPending} onClick={salvar}>
              {enviar.isPending ? 'Enviando…' : 'Salvar foto'}
            </Botao>
          </div>
        </div>
      ) : preparando ? (
        /* A previa esta sendo feita no navegador (p.32): o lugar dela vira a
           forma dela — a imagem e a linha da miniatura —, e nao uma frase
           embaixo da foto que ainda vai sair. */
        <Carregando oQue="Preparando a foto" className="flex flex-col gap-3">
          <Barra className="h-48 w-full rounded-controle" />
          <span className="flex items-center gap-3">
            <Barra className="size-8 rounded-badge" />
            <span className="flex min-w-0 flex-1 flex-col">
              <Barra className="my-1 h-3 w-40" />
              <Barra className="my-1 h-3 w-32" />
            </span>
          </span>
        </Carregando>
      ) : fotoId !== null ? (
        <div className="flex flex-col gap-3">
          {consulta.isPending ? (
            /* Carregando (p.32): o bloco da foto, na altura dela, e a linha de
               quem a enviou. */
            <Carregando oQue="Carregando a foto" className="flex flex-col gap-3">
              <Barra className="h-48 w-full rounded-controle" />
              <Barra className="my-1 h-3 w-3/5" />
            </Carregando>
          ) : consulta.isError ? (
            <div className="flex flex-col items-start gap-2">
              <MensagemDeErro erro={consulta.error} />
              <Botao
                nivel="secundario"
                onClick={() => {
                  void consulta.refetch();
                }}
              >
                Carregar a foto
              </Botao>
            </div>
          ) : dados && imagemFalhou === dados.url ? (
            <div className="flex flex-col items-start gap-2">
              <p className="txt-apoio text-pretty text-tinta-secundaria">
                A foto não carregou. O endereço dela vale 5 minutos; carregue de novo para pedir outro.
              </p>
              <Botao
                nivel="secundario"
                onClick={() => {
                  setImagemFalhou(null);
                  void consulta.refetch();
                }}
              >
                Carregar a foto
              </Botao>
            </div>
          ) : dados ? (
            /* O endereco chegou, a imagem ainda nao (p.32): ate ela abrir, o
               bloco do esqueleto na altura dela, para a ficha nao pular. */
            <img
              src={dados.url}
              alt={`Foto de ${equipamento.codigo}`}
              className={
                imagemAberta === dados.url
                  ? 'bloco-rebaixado max-h-48 w-full object-contain'
                  : 'esqueleto h-48 w-full rounded-controle object-contain'
              }
              onLoad={() => {
                setImagemAberta(dados.url);
              }}
              onError={() => {
                setImagemFalhou(dados.url);
              }}
            />
          ) : null}

          {dados ? (
            <p className="txt-apoio text-tinta-secundaria">
              {`Enviada em ${dados.foto.enviadoEm ? formatarDataHora(dados.foto.enviadoEm) : 'data não registrada'}${dados.foto.enviadoPor ? ` por ${dados.foto.enviadoPor}` : ''}`}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            {podeEditar ? (
              <Botao nivel="secundario" disabled={ocupado} onClick={escolherArquivo}>
                Trocar foto
              </Botao>
            ) : null}
            <Botao nivel="terciario" onClick={abrir}>
              Abrir foto
            </Botao>
            {podeEditar ? (
              <BotaoDestrutivo
                rotulo="Remover foto"
                pergunta={`Remover a foto de ${equipamento.codigo}?`}
                consequencia="A lista volta a mostrar o ícone do tipo de equipamento. A remoção fica registrada na linha do tempo da máquina."
                rotuloConfirmar="Remover foto"
                rotuloCancelar="Manter foto"
                aoConfirmar={removerFoto}
                {...(ocupado ? { motivoIndisponivel: 'Espere a foto terminar de subir ou de sair.' } : {})}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Sem foto. A lista mostra o ícone do tipo de equipamento.
          </p>
          {podeEditar ? (
            <Botao nivel="secundario" disabled={ocupado} onClick={escolherArquivo}>
              Enviar foto
            </Botao>
          ) : null}
        </div>
      )}

      {problema ? (
        <p role="alert" className="txt-apoio text-pretty text-parada">
          {problema}
        </p>
      ) : null}

      {avisarMiniatura ? (
        <p role="status" className="txt-apoio text-pretty text-tinta-secundaria">
          A foto foi salva, mas a miniatura da lista não subiu. Envie a mesma foto de novo para completar.
        </p>
      ) : null}

      <MensagemDeErro erro={recusa} />
    </section>
  );
}
